import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, FileText, Wallet, MapPin, Building2, Layers } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useGovCart } from '../../contexts/GovCartContext';
import { useAuth } from '../../contexts/AuthContext';
import { gov } from '../../lib/govApi';
import { portalPath } from '../../lib/portal';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOV_API = `${BACKEND_URL}/api/gov`;

const GovCart = () => {
  const { items, update, remove, total, clear } = useGovCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [deliveryLocationId, setDeliveryLocationId] = useState('');
  const [costCenters, setCostCenters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [divisionName, setDivisionName] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fetch CC (filtered to user's dept OR division) + DL (division-scoped) — auto-filled from server.
    axios.get(`${GOV_API}/my-division/cost-centers`).then((r) => setCostCenters(r.data || [])).catch(() => setCostCenters([]));
    axios.get(`${GOV_API}/my-division/delivery-locations`).then((r) => {
      const locs = r.data || [];
      setLocations(locs);
      const def = locs.find((l) => l.is_default);
      if (def) setDeliveryLocationId(def.id);
    }).catch(() => setLocations([]));
    // Auto-fill division + department name for the read-only header
    if (user?.division_id) {
      axios.get(`${GOV_API}/divisions/${user.division_id}`)
        .then((r) => setDivisionName(r.data?.name || ''))
        .catch(() => {});
    }
    if (user?.division_id) {
      axios.get(`${GOV_API}/my-division/departments`).then((r) => {
        const mine = (r.data || []).find((d) => d.id === user.department_id);
        if (mine) setDepartmentName(mine.name);
      }).catch(() => {});
    }
  }, [user]);

  const submit = async () => {
    if (items.length === 0) return;
    if (!costCenterId) { toast({ title: 'Select a Cost Center', variant: 'destructive' }); return; }
    if (!deliveryLocationId) { toast({ title: 'Select a Delivery Location', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const res = await gov.submitOrder({
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        po_number: poNumber || null,
        notes: notes || null,
        cost_center_id: costCenterId,
        delivery_location_id: deliveryLocationId,
      });
      clear();
      toast({ title: 'Order submitted', description: `Order ${res.data.order_id} is pending approval.` });
      navigate(portalPath(`/orders/${res.data.order_id}`));
    } catch (e) {
      toast({ title: 'Submission failed', description: e.response?.data?.detail || 'Could not submit order', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="gov-cart-empty">
        <div className="text-center">
          <ShoppingBag className="h-16 w-16 mx-auto text-slate-300" />
          <h2 className="text-xl font-bold text-slate-800 mt-3">Your procurement cart is empty</h2>
          <p className="text-slate-500 mt-1 mb-4">Add items from the DES catalog to start an order.</p>
          <Link to={portalPath('/catalog')}><Button className="bg-blue-700 hover:bg-blue-800 text-white">Browse Catalog</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="gov-cart-page">
      <div className="lg:col-span-2 space-y-3">
        <h1 className="text-2xl font-bold text-slate-800">Procurement Cart</h1>
        {items.map((i) => (
          <Card key={i.id} className="border-2" data-testid={`gov-cart-item-${i.id}`}>
            <CardContent className="p-4 flex gap-4 items-center">
              {i.image && <img src={i.image} alt={i.name} className="w-16 h-16 rounded object-cover" />}
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-sm">{i.name}</div>
                <div className="text-xs text-slate-500">SKU {i.sku} · {i.manufacturer}</div>
                <div className="text-blue-700 font-bold mt-1">${Number(i.price).toFixed(2)} <span className="text-[10px] uppercase text-emerald-700 ml-1">Contract</span></div>
              </div>
              <div className="flex items-center border-2 border-slate-200 rounded">
                <Button variant="ghost" size="sm" onClick={() => update(i.id, i.quantity - 1)}><Minus className="h-3.5 w-3.5" /></Button>
                <span className="px-3 font-semibold w-10 text-center">{i.quantity}</span>
                <Button variant="ghost" size="sm" onClick={() => update(i.id, i.quantity + 1)}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="text-right">
                <div className="font-bold text-slate-800">${(i.price * i.quantity).toFixed(2)}</div>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(i.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-2 lg:sticky lg:top-4 h-fit">
        <CardContent className="p-5 space-y-4">
          <h2 className="font-bold text-slate-800">Order Summary</h2>

          {(divisionName || departmentName) && (
            <div className="bg-slate-50 border rounded p-3 text-xs space-y-1" data-testid="cart-auto-filled">
              <div className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-blue-700" /><b>Division:</b> {divisionName || '—'}</div>
              <div className="flex items-center gap-1"><Layers className="h-3.5 w-3.5 text-purple-700" /><b>Department:</b> {departmentName || '—'}</div>
              <p className="text-[10px] text-slate-400 pt-1">Auto-filled from your profile</p>
            </div>
          )}

          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold">${total.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Tax (exempt)</span><span className="font-semibold text-emerald-700">$0.00</span></div>
          <div className="flex justify-between border-t pt-3"><span className="font-bold">Total</span><span className="text-blue-700 text-xl font-bold" data-testid="gov-cart-total">${total.toFixed(2)}</span></div>

          <div>
            <Label className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Cost Center *</Label>
            <Select value={costCenterId} onValueChange={setCostCenterId}>
              <SelectTrigger data-testid="cart-cost-center"><SelectValue placeholder={costCenters.length ? 'Select cost center' : 'No cost centers available'} /></SelectTrigger>
              <SelectContent>
                {costCenters.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {costCenters.length === 0 && <p className="text-[11px] text-amber-700 mt-1">Ask your Agency Admin to add cost centers for your division.</p>}
          </div>

          <div>
            <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Delivery Location *</Label>
            <Select value={deliveryLocationId} onValueChange={setDeliveryLocationId}>
              <SelectTrigger data-testid="cart-delivery-location"><SelectValue placeholder={locations.length ? 'Select delivery location' : 'No locations available'} /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}{l.is_default ? ' (default)' : ''} — {l.city}, {l.state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {locations.length === 0 && <p className="text-[11px] text-amber-700 mt-1">Ask your Agency Admin to add delivery locations for your division.</p>}
          </div>

          <div>
            <Label htmlFor="po">Purchase Order #</Label>
            <Input id="po" placeholder="e.g. PO-12345" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="gov-cart-po" />
          </div>

          <div>
            <Label htmlFor="notes">Notes to approver (optional)</Label>
            <Textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="gov-cart-notes" />
          </div>

          <Button onClick={submit} disabled={submitting || !costCenterId || !deliveryLocationId} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="gov-cart-submit">
            <FileText className="h-4 w-4 mr-2" /> {submitting ? 'Submitting…' : 'Submit Order for Approval'}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">Your order will be routed through your agency&apos;s approval workflow.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default GovCart;
